import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { BannerSlideDisplay } from '../../types/slider';

export type { BannerSlideDisplay };

type BannerSliderProps = {
  slides: BannerSlideDisplay[];
  autoPlayMs?: number;
  className?: string;
  minHeight?: string;
};

export default function BannerSlider({
  slides,
  autoPlayMs = 5000,
  className = '',
  minHeight = 'min-h-[280px] lg:min-h-[340px]',
}: BannerSliderProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback(
    (next: number) => setIndex((next + slides.length) % slides.length),
    [slides.length],
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(next, autoPlayMs);
    return () => clearInterval(id);
  }, [paused, next, autoPlayMs, slides.length]);

  if (slides.length === 0) {
    return null;
  }

  const slide = slides[index];
  const isHero = slide.variant !== 'promo';

  return (
    <div
      className={`relative overflow-hidden rounded-lg ${minHeight} ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute inset-0 ${slide.bgClass ?? 'bg-gradient-to-br from-red-50 via-white to-neutral-100'}`}
        >
          {slide.overlayClass && <div className={`absolute inset-0 ${slide.overlayClass}`} />}

          {isHero ? (
            <>
              <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-micron-red/5 blur-2xl" />
              <div className="relative z-10 flex h-full flex-col justify-center p-8 lg:p-10">
                {slide.subtitle && (
                  <span className="mb-2 text-xs font-bold uppercase tracking-widest text-micron-red">
                    {slide.subtitle}
                  </span>
                )}
                <h2 className={`max-w-xs text-2xl font-extrabold leading-tight sm:text-3xl lg:text-4xl ${slide.textClass ?? 'text-micron-black'}`}>
                  {slide.title}
                </h2>
                {slide.cta && (
                  <motion.a
                    href={slide.ctaHref ?? '#deals'}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="mt-5 inline-flex w-fit rounded-md bg-micron-red px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-micron-red-dark"
                  >
                    {slide.cta}
                  </motion.a>
                )}
              </div>
              <img
                src={slide.image}
                alt={slide.imageAlt ?? slide.title}
                className="absolute bottom-0 right-4 z-[1] h-[85%] w-auto object-contain sm:right-8"
              />
            </>
          ) : (
            <>
              <img
                src={slide.image}
                alt={slide.imageAlt ?? slide.title}
                className="h-full w-full object-cover"
              />
              <div className={`absolute inset-0 ${slide.overlayClass ?? 'bg-gradient-to-t from-micron-red/90 via-micron-red/40 to-transparent'}`} />
              <div className="absolute bottom-0 left-0 z-10 p-5 text-white">
                <p className="text-sm font-semibold leading-snug whitespace-pre-line">{slide.title}</p>
                {slide.subtitle && <p className="mt-1 text-xs text-white/80">{slide.subtitle}</p>}
                {slide.cta && (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="mt-3 rounded-md bg-white px-4 py-2 text-xs font-bold text-micron-red"
                  >
                    {slide.cta}
                  </motion.button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-micron-black shadow-md transition hover:bg-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-micron-black shadow-md transition hover:bg-white"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-5 bg-micron-red' : 'w-2 bg-white/70 hover:bg-white'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
